import Vue, { CreateElement } from 'src/vue'
import { Component, Prop } from 'vue-property-decorator'
import moment from 'moment'

import { DatePicker } from '@src/elements/input/date-picker'
import { generateAllDays, generateAllMonths, getCurrentMonthPeriods, getCurrentWeekPeriods } from '@src/util/functions'
import { Caption, Subtitle2 } from '@src/elements/typography/typography'
import { Option } from '@src/elements/select/option'
import { OptionGroup } from '@src/elements/select/option-group'
import { Select } from '@src/elements/select/select'
import { convertToLocalDate, convertToYYYYMMDD } from '@src/util/date-format'
import './time-switch.sass'
import { DateFilterType } from '@src/elements/time-switch/date-filter-type'

// Label format to be used in all following charts (based on the time frame filtering):
export enum DateFormat {
  WEEK = 'dddd,D MMM',
  MONTH = 'D MMM',
  YEAR = 'MMM'
}

@Component
export class TimeSwitcher extends Vue {

  @Prop({ required: true })
  private dateFilters: DateFilterType[]

  @Prop({ required: false })
  private change: (datePickerType: string, periods: any[]) => void

  @Prop({ required: false, default: 'left' })
  private align: 'left' | 'right'

  @Prop({ required: false, default: true })
  private allowFutureDate: boolean

  private datePickerType: DateFilterType
  private weekInterval: { weekNumber: number, dateInterval: string }

  private selectedMonthsFilter: string = '1'

  constructor () {
    super()
    this.datePickerType = this.dateFilters[0]
    if (this.datePickerType === DateFilterType.WEEK_NUMBER) {
      let currentWeek = getCurrentWeekPeriods()
      this.buildCurrentWeekDates(currentWeek[0].from, currentWeek[currentWeek.length - 1].to)
    }
  }

  /**
   * Build period arrays as {from, to} pairs
   * - week -> all days of the week
   * - month -> all days of the month
   * - year -> first days and last days of each month
   * - weekNumber -> weekNumber - dateInterval
   * - lastMonths - last month -> all days of the current month
   *              - last 3 months -> pairs of the day 3 months ago and today
   *              - last 6 months -> pairs of the day 6 months ago and today
   *              - last 12 months -> first days and last days of each month in the current year
   * - lastYear -> empty array []
   */
  setPeriods (startDate: Date | null, monthsNumber?: string) {
    // expect to receive a starting date for computing periods based on this date
    if (!startDate && this.datePickerType !== DateFilterType.LAST_MONTHS && this.datePickerType !== DateFilterType.LAST_YEAR) {
      return
    }

    let periods: any[] = []
    let start = new Date(startDate!)
    let end = new Date(start)
    if (monthsNumber || monthsNumber === '') this.selectedMonthsFilter = monthsNumber!

    switch (this.datePickerType) {
      case DateFilterType.WEEK:
        // picker bug fix - it sends the second day in the week, so extract one day:
        start.setDate(start.getDate() - 1)
        end.setDate(end.getDate() + 5)
        periods = generateAllDays(start, end)
        break

      case DateFilterType.MONTH:
        end = new Date(start.getFullYear(), start.getMonth() + 1, 0)
        periods = generateAllDays(start, end)
        break

      case DateFilterType.YEAR:
        periods = generateAllMonths(start)
        break

      case DateFilterType.WEEK_NUMBER:
        start.setDate(start.getDate() - 1)
        end.setDate(end.getDate() + 5)
        this.buildCurrentWeekDates(start, end)
        periods = [convertToYYYYMMDD(start)]
        break

      case DateFilterType.LAST_MONTHS:
        const today = new Date()
        const year = today.getFullYear()
        let month = today.getMonth()
        switch (this.selectedMonthsFilter) {
          case '1':
            periods = getCurrentMonthPeriods()
            break
          case '3':
            periods.push({
              from: convertToYYYYMMDD(new Date(year, month - 3, today.getDate())),
              to: convertToYYYYMMDD(today)
            })
            break
          case '6':
            periods.push({
              from: convertToYYYYMMDD(new Date(year, month - 6, today.getDate())),
              to: convertToYYYYMMDD(today)
            })
            break
          case '12':
            periods.push({
              from: convertToYYYYMMDD(new Date(year, month - 12, today.getDate())),
              to: convertToYYYYMMDD(today)
            })
            break
        }
        break

      case DateFilterType.LAST_YEAR:
        periods = []
        break

      default:
        console.log('undefined date picker type')
    }

    if (this.change) {
      this.change(this.datePickerType, periods)
      this.$store.dispatch('updateDatepickerActiveTypeFilter', this.datePickerType)
    }
  }

  buildCurrentWeekDates (start, end) {
    if (!isNaN(moment(start).isoWeek())) {
      // click outside triggers invalid date
      this.weekInterval = { weekNumber: moment(start).isoWeek(), dateInterval: `${convertToLocalDate(start)} - ${convertToLocalDate(end)}` }
      this.$forceUpdate()
    }
  }

  showDatePicker (type) {
    this.datePickerType = type

    // last year picker automatically triggers the period switch
    if (type === DateFilterType.LAST_YEAR) {
      this.setPeriods(null)
    }
  }

  render (h: CreateElement) {
    let filters = this.dateFilters.map((filter: DateFilterType) => {
      return <div class={`${this.datePickerType === filter ? 'selected' : ''} ${filter === DateFilterType.WEEK_NUMBER ? 'week-div' : 'week'}`}
                  onClick={() => this.showDatePicker(filter)}>
        <Subtitle2>
          {filter === DateFilterType.WEEK_NUMBER ? `${this.$t('WEEK')} ${this.weekInterval.weekNumber}`
            : filter === DateFilterType.LAST_MONTHS ? this.$t('WEEK')
              : filter === DateFilterType.LAST_YEAR ? this.$t('YEAR')
                : this.$t(filter.toUpperCase())}
        </Subtitle2>
        {filter === DateFilterType.WEEK_NUMBER ? <Caption class='week-dates'>{this.weekInterval.dateInterval}</Caption> : null}

        {filter !== DateFilterType.LAST_MONTHS && filter !== DateFilterType.LAST_YEAR ?
          <DatePicker
            editable={false}
            popperClass={`time-picker-${filter}`}
            class={`time-picker-${filter}`}
            type={filter === DateFilterType.WEEK_NUMBER ? 'week' : filter} change={(value: Date) => this.setPeriods(value)}
            format='' allowFutureDate={this.allowFutureDate}
            filter={filter}
          /> : null}

        {filter === DateFilterType.LAST_MONTHS ?
          <div class='last-months-options'>
            <Select>
              <OptionGroup>
                <Option type='single-option' value={'1'} label={this.$t('PAGE.TASKS.PERIOD_FILTER.1MONTHS')} select={(value) => this.setPeriods(null, value)} class={{ 'highlight': this.selectedMonthsFilter === '1' }} />
                <Option type='single-option' value={'3'} label={this.$t('PAGE.TASKS.PERIOD_FILTER.3MONTHS')} select={(value) => this.setPeriods(null, value)} class={{ 'highlight': this.selectedMonthsFilter === '3' }} />
                <Option type='single-option' value={'6'} label={this.$t('PAGE.TASKS.PERIOD_FILTER.6MONTHS')} select={(value) => this.setPeriods(null, value)} class={{ 'highlight': this.selectedMonthsFilter === '6' }} />
                <Option type='single-option' value={'12'} label={this.$t('PAGE.TASKS.PERIOD_FILTER.12MONTHS')} select={(value) => this.setPeriods(null, value)} class={{ 'highlight': this.selectedMonthsFilter === '12' }} />
              </OptionGroup>
            </Select>
          </div> : null}
      </div>
    })

    return <div class={`time-switch align-${this.align}`}>
      {filters}
    </div>
  }
}
